// src/components/payments/PaymentMethodsPanel.tsx
//
// A contact's saved payment methods: list, add, and choose a default.
//
// ============================================================================
// WHAT THIS PANEL CAN HONESTLY SHOW
// ============================================================================
//
// Not much, and that is correct. The Payments v1 contract publishes no card
// brand, no last4 and no expiry, and CRM stores none — so every saved method
// renders as a neutral "Saved payment method". Inventing "Visa ···· 4242"
// from a UUID would be a fabrication the user would reasonably act on.
//
// ============================================================================
// WHAT HAPPENS WHEN PROVISIONING IS OFF
// ============================================================================
//
// `PAYMENTS_INSTRUMENT_PROVISIONING_ENABLED` is a SERVER flag, so the browser
// cannot read it — and should not, because a client-side copy can disagree
// with the server that actually enforces it. The action is offered, the
// backend refuses with 503 `provisioning_disabled`, and that refusal is
// rendered plainly. The workspace's merchant configuration IS readable, so an
// unconfigured workspace is disabled up front rather than after a click.
import React, { useState } from 'react';
import {
  useGetContactInstrumentsQuery,
  useGetPaymentsConfigQuery,
  useSetDefaultInstrumentMutation,
  type ContactPaymentInstrument,
} from '../../api/paymentsIntegrationApi';
import { AddPaymentMethodDialog } from './AddPaymentMethodDialog';

function MethodRow({
  instrument,
  contactId,
  busy,
}: {
  instrument: ContactPaymentInstrument;
  contactId: string;
  busy: boolean;
}): React.ReactElement {
  const [setDefault, { isLoading }] = useSetDefaultInstrumentMutation();

  return (
    <li
      className="flex items-center justify-between gap-3 border-b border-slate-100 py-3 last:border-0"
      data-testid="payment-method-row"
    >
      <div className="min-w-0">
        {/* Neutral by necessity: CRM holds no brand, last4 or expiry. */}
        <p className="text-sm font-medium text-slate-900">Saved payment method</p>
        <p className="truncate text-xs text-slate-500" title={instrument.paymentInstrumentId}>
          Added {new Date(instrument.createdAt).toLocaleDateString()}
        </p>
      </div>

      {instrument.isDefault ? (
        <span
          className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200"
          data-testid="default-badge"
        >
          Default
        </span>
      ) : (
        <button
          type="button"
          className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          disabled={isLoading || busy}
          onClick={() => {
            // A CRM-only preference change. It calls no provider, revokes
            // nothing at Payments, and selects no processor.
            void setDefault({
              contactId,
              paymentInstrumentId: instrument.paymentInstrumentId,
            });
          }}
        >
          Make default
        </button>
      )}
    </li>
  );
}

export function PaymentMethodsPanel({ contactId }: { contactId: string }): React.ReactElement {
  const [adding, setAdding] = useState(false);
  const { data: config } = useGetPaymentsConfigQuery();
  const {
    data: instruments = [],
    isLoading,
    isError,
  } = useGetContactInstrumentsQuery({ contactId });

  const merchantConfigured = config?.configured === true;

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <header className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Payment methods</h3>
        <button
          type="button"
          className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          disabled={!merchantConfigured}
          onClick={() => setAdding(true)}
          data-testid="add-payment-method"
        >
          Add payment method
        </button>
      </header>

      {!merchantConfigured && (
        <p className="mb-3 text-xs text-amber-800" data-testid="merchant-not-configured">
          This workspace is not set up to accept payments yet. An administrator needs to configure
          it before cards can be saved.
        </p>
      )}

      {isLoading && <p className="text-sm text-slate-500">Loading…</p>}
      {isError && <p className="text-sm text-rose-700">Could not load saved payment methods.</p>}

      {!isLoading && !isError && instruments.length === 0 && (
        <p className="text-sm text-slate-500">No saved payment methods.</p>
      )}

      {instruments.length > 0 && (
        <ul className="mt-1">
          {instruments.map((i) => (
            <MethodRow key={i.id} instrument={i} contactId={contactId} busy={adding} />
          ))}
        </ul>
      )}

      {adding && (
        <AddPaymentMethodDialog contactId={contactId} onClose={() => setAdding(false)} />
      )}
    </section>
  );
}
